import { useCallback, useEffect, useState } from "react";
import { api, apiError, fmt, hhmm } from "@/lib/api";
import { NavBar } from "@/components/NavBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Ticket, Clock, Trophy, Sparkles, PartyPopper, Users } from "lucide-react";

const STATUS_LABEL = {
  pending_claim: "Claim it",
  claimed: "Claim submitted",
  fulfilled: "Delivered",
  expired: "Expired",
  rejected: "Rejected",
};

const EMPTY_CLAIM = {
  character_name: "",
  discord_tag: "",
  full_name: "",
  age: "",
  address_line: "",
  city: "",
  state: "",
  postal_code: "",
  country: "",
  note: "",
};

function useCountdown(iso) {
  const [left, setLeft] = useState("");
  useEffect(() => {
    if (!iso) return;
    const tick = () => {
      const ms = new Date(iso) - new Date();
      if (ms <= 0) return setLeft("drawing now");
      const d = Math.floor(ms / 86400000);
      const h = Math.floor((ms % 86400000) / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      const parts = [];
      if (d > 0) parts.push(`${d}d`);
      if (d > 0 || h > 0) parts.push(`${h}h`);
      parts.push(`${m}m`, `${s}s`);
      setLeft(parts.join(" "));
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [iso]);
  return left;
}

const Stat = ({ label, value, hint, testid }) => (
  <div className="border border-border bg-card p-6" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{label}</div>
    <div className="mono mt-3 text-3xl font-semibold text-primary">{value}</div>
    {hint && <div className="mt-2 text-xs text-muted-foreground">{hint}</div>}
  </div>
);

export default function Giveaway() {
  const { setUser } = useAuth();
  const [g, setG] = useState(null);
  const [buying, setBuying] = useState(1);
  const [claimFor, setClaimFor] = useState(null);
  const [form, setForm] = useState(EMPTY_CLAIM);
  const [saving, setSaving] = useState(false);
  const countdown = useCountdown(g?.draws_at);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/giveaway");
      setG(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const buy = async () => {
    try {
      const { data } = await api.post("/giveaway/buy-entries", { count: Number(buying) });
      setUser(data.user);
      toast.success(`${data.entries_bought} extra entr${data.entries_bought > 1 ? "ies" : "y"} added`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitClaim = async () => {
    setSaving(true);
    try {
      await api.post(`/giveaway/claim/${claimFor.id}`, { ...form, age: Number(form.age) });
      toast.success("Claim submitted — staff will take it from here");
      setClaimFor(null);
      setForm(EMPTY_CLAIM);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!g) return <div className="min-h-screen bg-background"><NavBar /></div>;

  const odds = g.entries_total > 0 ? ((g.my_entries / g.entries_total) * 100).toFixed(1) : "0.0";

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      <main className="mx-auto max-w-7xl px-6 py-12" data-testid="giveaway-page">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <h1 className="text-4xl font-semibold sm:text-5xl">{g.prize.prize_title}</h1>
            <p className="mt-4 max-w-2xl text-base text-muted-foreground">
              {g.prize.prize_description ||
                `Every ${hhmm(g.minutes_per_entry)} you play earns one entry.`}
            </p>
          </div>
          <div className="border border-primary/40 bg-primary/5 px-6 py-4 text-right" data-testid="giveaway-countdown">
            <div className="flex items-center justify-end gap-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <Clock className="h-3 w-3" /> Draw in
            </div>
            <div className="mono mt-2 text-2xl font-semibold text-primary">{countdown}</div>
            <div className="mt-1 text-xs text-muted-foreground">
              {new Date(g.draws_at).toLocaleString()} · {g.winners_count} winner
              {g.winners_count > 1 ? "s" : ""}
            </div>
          </div>
        </div>

        {!g.enabled && (
          <div className="mt-8 border border-amber-500/40 bg-amber-500/10 p-5 text-sm text-amber-300" data-testid="giveaway-closed">
            The giveaway is paused right now — your playtime still banks points.
          </div>
        )}

        <div className="mt-10 grid gap-4 md:grid-cols-4">
          <Stat
            label="Your entries"
            value={fmt(g.my_entries)}
            hint={g.my_purchased ? `${g.my_purchased} bought with points` : `${odds}% of the pot`}
            testid="my-entries-stat"
          />
          <Stat
            label="Played this round"
            value={hhmm(g.my_minutes)}
            hint={`${g.my_next_entry_in}m to your next entry`}
            testid="my-minutes-stat"
          />
          <Stat label="Total entries" value={fmt(g.entries_total)} hint={`${g.participants} players in`} testid="total-entries-stat" />
          <Stat label="Your odds" value={`${odds}%`} hint={`${g.cooldown_days}-day cooldown after a win`} testid="odds-stat" />
        </div>

        {g.in_cooldown && (
          <div className="mt-4 border border-border bg-secondary/40 p-4 text-sm text-muted-foreground" data-testid="cooldown-notice">
            You won recently, so you're sitting out the next {g.cooldown_days} day
            {g.cooldown_days > 1 ? "s" : ""} to spread the love. Keep playing — entries still count after that.
          </div>
        )}

        {g.extra_entry_points > 0 && (
          <div className="mt-6 flex flex-wrap items-end gap-4 border border-border bg-card p-6" data-testid="buy-entries-card">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <h3 className="text-lg font-semibold">Boost your odds</h3>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                {fmt(g.extra_entry_points)} points per extra entry · max {g.max_entries_per_period ? "" : ""}
                {g.max_extra_entries} per draw · you've bought {g.my_purchased}
              </p>
            </div>
            <div className="w-28">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Entries</Label>
              <Input
                type="number"
                min="1"
                data-testid="buy-entries-count"
                className="mt-2 rounded-none bg-secondary mono"
                value={buying}
                onChange={(e) => setBuying(e.target.value)}
              />
            </div>
            <Button className="rounded-none" onClick={buy} data-testid="buy-entries-btn">
              <Ticket className="mr-2 h-4 w-4" /> Buy for {fmt(g.extra_entry_points * Number(buying || 0))}
            </Button>
          </div>
        )}

        {g.my_wins.length > 0 && (
          <section className="mt-12" data-testid="my-wins-section">
            <h2 className="text-base font-semibold md:text-lg">Your wins</h2>
            <div className="mt-4 space-y-3">
              {g.my_wins.map((w) => (
                <div
                  key={w.id}
                  className="flex flex-wrap items-center justify-between gap-4 border border-border bg-card p-5"
                  data-testid={`win-row-${w.id}`}
                >
                  <div>
                    <div className="flex items-center gap-2 font-semibold">
                      <PartyPopper className="h-4 w-4 text-primary" /> {w.prize_title}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Drawn {new Date(w.drawn_at).toLocaleString()} · {w.entries} entries
                      {w.status === "pending_claim" &&
                        ` · claim by ${new Date(w.claim_deadline).toLocaleString()}`}
                    </div>
                    {w.admin_note && <div className="mt-1 text-xs text-muted-foreground">Staff: {w.admin_note}</div>}
                  </div>
                  {w.status === "pending_claim" ? (
                    <Button
                      className="rounded-none"
                      onClick={() => {
                        setClaimFor(w);
                        setForm(EMPTY_CLAIM);
                      }}
                      data-testid={`claim-btn-${w.id}`}
                    >
                      Claim prize
                    </Button>
                  ) : (
                    <Badge variant="secondary" className="rounded-none uppercase" data-testid={`win-status-${w.id}`}>
                      {STATUS_LABEL[w.status] || w.status}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="mt-12 grid items-stretch gap-6 lg:grid-cols-2">
          <section className="flex flex-col" data-testid="entry-leaders">
            <h2 className="flex items-center gap-2 text-base font-semibold md:text-lg">
              <Users className="h-4 w-4 text-primary" /> Most entries this round
            </h2>
            <div className="mt-4 min-h-[88px] flex-1 border border-border bg-card">
              {g.leaders.length === 0 && (
                <p className="px-5 py-3 text-sm text-muted-foreground">
                  Nobody has an entry yet — first hour played takes the lead.
                </p>
              )}
              {g.leaders.map((l, i) => (
                <div key={l.username + i} className="flex items-center justify-between border-b border-border px-5 py-3 text-sm last:border-b-0">
                  <span className="flex items-center gap-3">
                    <span className="mono text-xs text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                    {l.username}
                  </span>
                  <span className="mono text-primary">{fmt(l.entries)}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="flex flex-col" data-testid="winners-wall">
            <h2 className="flex items-center gap-2 text-base font-semibold md:text-lg">
              <Trophy className="h-4 w-4 text-primary" /> Recent winners
            </h2>
            <div className="mt-4 min-h-[88px] flex-1 border border-border bg-card">
              {g.recent_winners.length === 0 && (
                <p className="px-5 py-3 text-sm text-muted-foreground">No draws yet — you could be the first name here.</p>
              )}
              {g.recent_winners.map((w) => (
                <div key={`${w.drawn_at}-${w.username}`} className="flex items-center justify-between border-b border-border px-5 py-3 text-sm last:border-b-0">
                  <span>
                    <span className="font-semibold">{w.username}</span>
                    <span className="text-muted-foreground"> · {w.prize_title}</span>
                  </span>
                  <span className="mono text-xs text-muted-foreground">
                    {new Date(w.drawn_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Dialog open={!!claimFor} onOpenChange={(o) => !o && setClaimFor(null)}>
        <DialogContent className="max-w-2xl bg-card" data-testid="claim-dialog">
          <DialogHeader>
            <DialogTitle>Claim: {claimFor?.prize_title}</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Staff only use this to get the prize to you. Shipping details are visible to admins only.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 grid max-h-[55vh] gap-3 overflow-auto pr-1 md:grid-cols-2">
            {[
              ["character_name", "Character name", "text"],
              ["discord_tag", "Discord tag", "text"],
              ["full_name", "Full legal name", "text"],
              ["age", "Age", "number"],
              ["address_line", "Street address", "text"],
              ["city", "City", "text"],
              ["state", "State / region", "text"],
              ["postal_code", "Postal code", "text"],
              ["country", "Country", "text"],
            ].map(([key, label, type]) => (
              <div key={key}>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</Label>
                <Input
                  type={type}
                  data-testid={`claim-${key}`}
                  className="mt-2 rounded-none bg-secondary"
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                />
              </div>
            ))}
            <div className="md:col-span-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Note for staff</Label>
              <Textarea
                data-testid="claim-note"
                className="mt-2 rounded-none bg-secondary"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
              />
            </div>
          </div>
          <Button
            className="mt-4 w-full rounded-none"
            disabled={saving || !form.character_name || !form.full_name || !form.age || !form.address_line}
            onClick={submitClaim}
            data-testid="submit-claim-btn"
          >
            {saving ? "Sending…" : "Submit claim"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
